// src/pages/shipping/components/RateRowTable.tsx
/* Movatic WMS — Frontend | CesarFilho 932048016
   Gerenciador de linhas (RateRows) — React-Query v5 + UI stubs.
*/
import React, { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Button, Input, Label, Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle } from "@/components/ui";

type RateRow = {
    id: number;
    rate_table: number;
    min_weight?: string | null;
    max_weight?: string | null;
    min_volume?: string | null;
    max_volume?: string | null;
    price: string;
    currency?: string | null;
    transit_days?: number | null;
};

type Paged<T> = { count: number; next: string | null; previous: string | null; results: T[] };
const PAGE_SIZE = 10;

export default function RateRowTable({ tableId }: { tableId: number }) {
    const qc = useQueryClient();
    const [page, setPage] = useState(1);
    const [openAdd, setOpenAdd] = useState(false);
    const [editRow, setEditRow] = useState<RateRow | null>(null);

    useEffect(() => { setPage(1); }, [tableId]);

    const qRows = useQuery<Paged<RateRow>>({
        queryKey: ["rate-rows", { tableId, page }],
        queryFn: async () => {
            const { data } = await api.get(`/api/shipping/rate-tables/${tableId}/rows/`, {
                params: { page, page_size: PAGE_SIZE },
            });
            if (Array.isArray(data)) {
                return { count: data.length, next: null, previous: null, results: data as RateRow[] };
            }
            return data as Paged<RateRow>;
        },
        placeholderData: (prev) => prev,
        staleTime: 10_000,
    });

    const total = qRows.data?.count ?? 0;
    const rows = qRows.data?.results ?? [];
    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

    const add = useMutation({
        mutationFn: async (payload: any) =>
            api.post(`/api/shipping/rate-tables/${tableId}/rows/`, payload),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["rate-rows"] });
            setOpenAdd(false);
        },
    });

    const save = useMutation({
        mutationFn: async () => {
            if (!editRow) return;
            const payload = {
                min_weight: editRow.min_weight || null,
                max_weight: editRow.max_weight || null,
                min_volume: editRow.min_volume || null,
                max_volume: editRow.max_volume || null,
                price: editRow.price,
                currency: editRow.currency || "EUR",
                transit_days: editRow.transit_days ?? null,
            };
            return api.patch(`/api/shipping/rate-rows/${editRow.id}/`, payload);
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["rate-rows"] });
            setEditRow(null);
        },
    });

    const del = useMutation({
        mutationFn: async (id: number) => api.delete(`/api/shipping/rate-rows/${id}/`),
        onSuccess: () => qc.invalidateQueries({ queryKey: ["rate-rows"] }),
    });

    return (
        <div className="p-4 space-y-3">
            <div className="flex items-center justify-between">
                <h4 className="font-semibold">Faixas / Regras</h4>

                <Button size="sm" onClick={() => setOpenAdd(true)}>+ Adicionar</Button>
                <Dialog open={openAdd} onOpenChange={setOpenAdd}>
                    <DialogContent className="max-w-lg">
                        <DialogHeader><DialogTitle>Nova linha</DialogTitle></DialogHeader>
                        <RowForm onSubmit={(payload) => add.mutate(payload)} submitting={add.isPending} />
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setOpenAdd(false)}>Cancelar</Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>

            <div className="overflow-auto border rounded">
                <table className="w-full text-sm">
                    <thead className="bg-slate-900/30">
                        <tr>
                            <th className="text-left p-2">Peso min</th>
                            <th className="text-left p-2">Peso máx</th>
                            <th className="text-left p-2">Vol. min</th>
                            <th className="text-left p-2">Vol. máx</th>
                            <th className="text-left p-2">Preço</th>
                            <th className="text-left p-2">Moeda</th>
                            <th className="text-left p-2">Transit (dias)</th>
                            <th className="text-right p-2">Ações</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.length === 0 && (
                            <tr><td colSpan={8} className="p-6 text-center opacity-70">Nenhuma linha.</td></tr>
                        )}
                        {rows.map((r: RateRow) => (
                            <tr key={r.id} className="border-t border-slate-800">
                                <td className="p-2">{r.min_weight ?? "—"}</td>
                                <td className="p-2">{r.max_weight ?? "—"}</td>
                                <td className="p-2">{r.min_volume ?? "—"}</td>
                                <td className="p-2">{r.max_volume ?? "—"}</td>
                                <td className="p-2 font-medium">{r.price}</td>
                                <td className="p-2">{r.currency ?? "EUR"}</td>
                                <td className="p-2">{r.transit_days ?? "—"}</td>
                                <td className="p-2 text-right">
                                    <div className="inline-flex gap-2">
                                        <Button size="sm" variant="outline" onClick={() => setEditRow(r)}>Editar</Button>
                                        <Button size="sm" variant="danger" onClick={() => del.mutate(r.id)} disabled={del.isPending}>
                                            Excluir
                                        </Button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Dialog de edição */}
            <Dialog open={!!editRow} onOpenChange={(v) => !v && setEditRow(null)}>
                <DialogContent className="max-w-lg">
                    <DialogHeader><DialogTitle>Editar linha</DialogTitle></DialogHeader>
                    {editRow && (
                        <RowForm
                            initial={editRow}
                            onChange={setEditRow}
                            onSubmit={() => save.mutate()}
                            submitting={save.isPending}
                        />
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setEditRow(null)}>Cancelar</Button>
                        <Button onClick={() => save.mutate()} disabled={save.isPending}>Salvar</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <div className="flex items-center justify-between text-sm">
                <div>
                    {total ? `${(page - 1) * PAGE_SIZE + 1}–${Math.min(page * PAGE_SIZE, total)} de ${total}` : "—"}
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}>
                        Anterior
                    </Button>
                    <div className="px-2 py-1 border rounded">Página {page} / {totalPages}</div>
                    <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages}>
                        Próxima
                    </Button>
                </div>
            </div>
        </div>
    );
}

function RowForm({
    initial,
    onChange,
    onSubmit,
    submitting,
}: {
    initial?: Partial<RateRow>;
    onChange?: (v: RateRow) => void;
    onSubmit: (payload: any) => void;
    submitting?: boolean;
}) {
    const [f, setF] = useState<Partial<RateRow>>({
        min_weight: "",
        max_weight: "",
        min_volume: "",
        max_volume: "",
        price: "",
        currency: "EUR",
        transit_days: undefined,
    });

    useEffect(() => {
        if (initial) setF(initial);
    }, [initial]);

    useEffect(() => {
        if (onChange) onChange(f as RateRow);
    }, [f, onChange]);

    const onTxt =
        (key: keyof RateRow) =>
            (e: React.ChangeEvent<HTMLInputElement>) =>
                setF((s) => ({ ...s, [key]: e.target.value }));

    const onTransit = (e: React.ChangeEvent<HTMLInputElement>) =>
        setF((s) => ({
            ...s,
            transit_days: e.target.value === "" ? undefined : Number(e.target.value) || undefined,
        }));

    return (
        <div className="grid gap-3">
            <div className="grid grid-cols-2 gap-3">
                <div className="grid gap-1">
                    <Label>Peso min (kg)</Label>
                    <Input value={f.min_weight ?? ""} onChange={onTxt("min_weight")} />
                </div>
                <div className="grid gap-1">
                    <Label>Peso máx (kg)</Label>
                    <Input value={f.max_weight ?? ""} onChange={onTxt("max_weight")} />
                </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
                <div className="grid gap-1">
                    <Label>Volume min (m³)</Label>
                    <Input value={f.min_volume ?? ""} onChange={onTxt("min_volume")} />
                </div>
                <div className="grid gap-1">
                    <Label>Volume máx (m³)</Label>
                    <Input value={f.max_volume ?? ""} onChange={onTxt("max_volume")} />
                </div>
            </div>

            <div className="grid grid-cols-3 gap-3">
                <div className="grid gap-1">
                    <Label>Preço</Label>
                    <Input value={f.price ?? ""} onChange={onTxt("price")} />
                </div>
                <div className="grid gap-1">
                    <Label>Moeda</Label>
                    <Input value={f.currency ?? "EUR"} onChange={onTxt("currency")} />
                </div>
                <div className="grid gap-1">
                    <Label>Transit (dias)</Label>
                    <Input value={String(f.transit_days ?? "")} onChange={onTransit} />
                </div>
            </div>

            <div className="flex justify-end gap-2">
                <Button
                    onClick={() =>
                        onSubmit({
                            min_weight: f.min_weight || null,
                            max_weight: f.max_weight || null,
                            min_volume: f.min_volume || null,
                            max_volume: f.max_volume || null,
                            price: f.price,
                            currency: f.currency || "EUR",
                            transit_days: f.transit_days ?? null,
                        })
                    }
                    disabled={!!submitting}
                >
                    {submitting ? "Salvando..." : "Salvar"}
                </Button>
            </div>
        </div>
    );
}
